import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/CheckCircle';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import ListIcon from '@mui/icons-material/List';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import WorkIcon from '@mui/icons-material/Work';

import React, { useContext, useEffect, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ServiceTitanAdjustableCapacityDTO } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Card } from '_lib/components/Card/Card';
import { DayCalendarSkeleton } from '_lib/components/DayCalendarSkeleton/DayCalendarSkeleton';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { List } from '_lib/components/List/List';
import { ListItem, ListItemProps } from '_lib/components/ListItem/ListItem';
import { ListItemButton } from '_lib/components/ListItemButton/ListItemButton';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { DateCalendar } from '_lib/components/Pickers/DateCalendar/DateCalendar';
import { Stack } from '_lib/components/Stack/Stack';
import { ToggleButton } from '_lib/components/ToggleButton/ToggleButton';
import { ToggleButtonGroup } from '_lib/components/ToggleButtonGroup/ToggleButtonGroup';
import { Typography } from '_lib/components/Typography/Typography';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { VerticalDivider } from '_lib/styles/PhoneCall.styled';
import { CustomerContext, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { customerDisplayName, formatAddressOneLine } from '_lib/utils/helpers';

import { logDebug } from '_lib/utils/logging';

import { StCapacityCompanyContext } from '../../../common/CompanyPane/Company.slice';
import { AvailabilityPercentage } from '../featureComponents/CapacityBooking/AvailabilityPercentage/AvailabilityPercentage';
import { AvailableTime } from '../featureComponents/CapacityBooking/AvailableTime/AvailableTime';
import { PhoneCallContext } from '../StCapacityPhoneCall.slice';

const sortCapacityItems = (a: ServiceTitanAdjustableCapacityDTO, b: ServiceTitanAdjustableCapacityDTO) => {
  if (a.start === b.start) {
    return a.end < b.end ? -1 : 1;
  }
  return a.start < b.start ? -1 : 1;
};

/**
 * @returns `YYYY-MM-DD` date-strings for start of month, and first day of next month
 */
const getStartAndEndOfMonth = (date: Date) => {
  const startDateTime = DateUtils.toStartOf(date, 'month', { timezone: 'utc' });
  const endDateTime = DateUtils.add(startDateTime, { months: 1 }, { timezone: 'utc' });

  return {
    startDate: DateUtils.toDateString(startDateTime, { timezone: 'utc' }),
    endDate: DateUtils.toDateString(endDateTime, { timezone: 'utc' })
  };
};

type GetAvailabilityDialogProps = AsyncDialogProps<{ capacityItem: ServiceTitanAdjustableCapacityDTO }> & {
  customer: CustomerContext['customer'];
  location: NonNullable<PhoneCallContext['location']>;
  jobType: NonNullable<PhoneCallContext['jobType']>;
  businessUnit: NonNullable<PhoneCallContext['businessUnit']>;
  companyContext: StCapacityCompanyContext;
  durationStart: PhoneCallURLState['durationStart'];
};

const AvailabilityListItem = (
  props: ListItemProps &
    Pick<GetAvailabilityDialogProps, 'companyContext'> & {
      displayMode: 'firstAvailable' | 'allAvailable';
      selectedCapacityItem: ServiceTitanAdjustableCapacityDTO | null;
      capacityItem: ServiceTitanAdjustableCapacityDTO;
      setCapacityItem: React.Dispatch<React.SetStateAction<ServiceTitanAdjustableCapacityDTO | null>>;
    }
) => {
  const { companyContext, displayMode, selectedCapacityItem, capacityItem, setCapacityItem, ...listItemProps } = props;

  return (
    <ListItem {...listItemProps}>
      <ListItemButton
        selected={!!selectedCapacityItem && capacityItem.start === selectedCapacityItem.start}
        onClick={() => {
          setCapacityItem(capacityItem);
        }}>
        <ListItemText
          sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}
          primary={
            <>
              <Stack direction='row' gap={0.5} alignItems='center'>
                {displayMode === 'firstAvailable' && (
                  <>
                    <Typography
                      text={DateUtils.format(capacityItem.start, DateFormatEnum.WeekdayUnabbreviated, {
                        timezone: 'utc'
                      })}
                    />
                    <VerticalDivider />
                    <Typography
                      text={DateUtils.format(capacityItem.start, DateFormatEnum.DateShort, {
                        timezone: 'utc'
                      })}
                    />
                    <VerticalDivider />
                  </>
                )}
                <AvailableTime capacityItem={capacityItem} companyTimeZone={companyContext.company.timezone} />
                <VerticalDivider />
                <AvailabilityPercentage capacityItem={capacityItem} />
              </Stack>
              {!!selectedCapacityItem && capacityItem.start === selectedCapacityItem.start && (
                <CheckIcon color='primary' />
              )}
            </>
          }
          disableTypography
        />
      </ListItemButton>
    </ListItem>
  );
};

type FirstAvailableProps = Pick<GetAvailabilityDialogProps, 'jobType' | 'businessUnit' | 'companyContext'> & {
  startOfCall: Date;
  selectedCapacityItem: ServiceTitanAdjustableCapacityDTO | null;
  setCapacityItem: React.Dispatch<React.SetStateAction<ServiceTitanAdjustableCapacityDTO | null>>;
  setIncrementStartingMonth: React.Dispatch<React.SetStateAction<boolean>>;
};

const FirstAvailable = (props: FirstAvailableProps) => {
  const {
    jobType,
    businessUnit,
    companyContext,
    startOfCall,
    selectedCapacityItem,
    setCapacityItem,
    setIncrementStartingMonth
  } = props;

  const serviceTitanApi = useContext(ApiContext).serviceTitan(companyContext.company.id);

  const firstMonthDateBounds = getStartAndEndOfMonth(startOfCall);
  const firstAvailableQuery = serviceTitanApi.adjustableCapacity.useUnpagedList({
    params: {
      businessUnitIds: [businessUnit.providerId],
      jobTypeId: jobType.providerId,
      startDate: firstMonthDateBounds.startDate,
      endDate: firstMonthDateBounds.endDate
    }
  });

  const nextMonth = DateUtils.add(startOfCall, { months: 1 });

  const [enableNextMonthLookup, setEnableNextMonthLookup] = useState(false);
  const secondMonthDateBounds = getStartAndEndOfMonth(nextMonth);
  const nextMonthAvailableQuery = serviceTitanApi.adjustableCapacity.useUnpagedList(
    {
      params: {
        businessUnitIds: [businessUnit.providerId],
        jobTypeId: jobType.providerId,
        startDate: secondMonthDateBounds.startDate,
        endDate: secondMonthDateBounds.endDate
      }
    },
    { enabled: enableNextMonthLookup }
  );

  useEffect(() => {
    if (firstAvailableQuery.data && !firstAvailableQuery.isFetching) {
      const needNextMonthLookup = firstAvailableQuery.data.length < 5;

      logDebug('needNextMonthLookup: ', needNextMonthLookup);

      setEnableNextMonthLookup(needNextMonthLookup);
      setIncrementStartingMonth(needNextMonthLookup);
    }
  }, [firstAvailableQuery.data, firstAvailableQuery.isFetching, setIncrementStartingMonth, setEnableNextMonthLookup]);

  const isFetching = firstAvailableQuery.isFetching || nextMonthAvailableQuery.isFetching;

  const capacityItems = [
    ...(firstAvailableQuery.data ? firstAvailableQuery.data : []),
    ...(nextMonthAvailableQuery.data ? nextMonthAvailableQuery.data : [])
  ];

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        minHeight: '346px',
        paddingBottom: 1
      }}>
      <BackdropSpinner open={isFetching} sx={{ alignItems: 'flex-start', paddingTop: 32 }} />
      {!!capacityItems.length && (
        <Card variant='outlined'>
          <List sx={{ overflow: 'auto', width: '100%' }} disablePadding>
            {capacityItems
              .sort(sortCapacityItems)
              .slice(0, 5)
              .map((capacityItem, index) => (
                <AvailabilityListItem
                  divider={index !== 4}
                  key={index}
                  companyContext={companyContext}
                  displayMode={'firstAvailable'}
                  selectedCapacityItem={selectedCapacityItem}
                  capacityItem={capacityItem}
                  setCapacityItem={setCapacityItem}
                />
              ))}
          </List>
        </Card>
      )}
      {!isFetching && !capacityItems.length && (
        <ErrorDisplay errors={['No Capacity for 2 months. Check company configuration.']} />
      )}
    </FlexBox>
  );
};

type AllAvailableProps = Pick<GetAvailabilityDialogProps, 'jobType' | 'businessUnit' | 'companyContext'> & {
  startOfCall: Date;
  selectedDay: Date | null;
  /**
   * Used to increment the starting selected month if the next 5 available days are not in the current month
   */
  incrementStartingMonth?: boolean;
  setSelectedDay: React.Dispatch<React.SetStateAction<Date | null>>;
  selectedCapacityItem: ServiceTitanAdjustableCapacityDTO | null;
  setCapacityItem: React.Dispatch<React.SetStateAction<ServiceTitanAdjustableCapacityDTO | null>>;
  fullScreen: boolean;
};

const availableDaysSet = new Set<number>();

const AllAvailable = (props: AllAvailableProps) => {
  const {
    jobType,
    businessUnit,
    startOfCall,
    selectedDay,
    incrementStartingMonth,
    setSelectedDay,
    selectedCapacityItem,
    setCapacityItem,
    companyContext,
    fullScreen
  } = props;

  const [selectedMonth, setSelectedMonth] = useState(
    selectedDay || incrementStartingMonth ? DateUtils.add(startOfCall, { months: 1 }) : startOfCall
  );

  const serviceTitanApi = useContext(ApiContext).serviceTitan(companyContext.company.id);

  const allAvailableDateBounds = getStartAndEndOfMonth(selectedDay || selectedMonth);
  const allAvailableQuery = serviceTitanApi.adjustableCapacity.useUnpagedList({
    params: {
      businessUnitIds: [businessUnit.providerId],
      jobTypeId: jobType.providerId,
      startDate: allAvailableDateBounds.startDate,
      endDate: allAvailableDateBounds.endDate
    }
  });

  useEffect(() => {
    if (allAvailableQuery.data && !allAvailableQuery.isFetching && !selectedDay) {
      availableDaysSet.clear();
      allAvailableQuery.data.map((capacityItem) => {
        const dayOfMonth = DateUtils.getDayOfMonth(new Date(capacityItem.start), { timezone: 'utc' });
        if (!availableDaysSet.has(dayOfMonth)) availableDaysSet.add(dayOfMonth);
      });
      logDebug('Available Days of month: ', availableDaysSet.values());

      const firstAvailableDayOfMonth = [...availableDaysSet].sort((a, b) => a - b)[0];

      if (firstAvailableDayOfMonth) {
        const firstAvailableDate = DateUtils.add(
          DateUtils.toStartOf(selectedMonth, 'month', { timezone: companyContext.company.timezone }),
          { days: firstAvailableDayOfMonth - 1 }
        );
        setSelectedDay(firstAvailableDate);
        logDebug('Available Day auto-selected: ', firstAvailableDate);
      } else {
        logDebug('Available could not be auto-selected: ', availableDaysSet);
        setSelectedDay(null);
      }
    }
  }, [
    allAvailableQuery.data,
    allAvailableQuery.isFetching,
    companyContext.company.timezone,
    selectedDay,
    selectedMonth,
    setSelectedDay
  ]);

  return (
    <FlexBox
      sx={{
        flexDirection: fullScreen ? 'column' : 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        minHeight: '346px',
        gap: fullScreen ? 2.5 : undefined,
        paddingBottom: 1
      }}>
      <BackdropSpinner open={allAvailableQuery.isFetching} sx={{ alignItems: 'flex-start', paddingTop: 32 }} />
      <Card
        variant='outlined'
        sx={[(theme) => ({ backgroundColor: theme.palette.background.paper, overflow: 'visible', height: '338px' })]}>
        <DateCalendar
          loading={allAvailableQuery.isFetching}
          renderLoading={() => <DayCalendarSkeleton />}
          shouldDisableDate={(day) => {
            return !availableDaysSet.has(day.day);
          }}
          views={['day']}
          disablePast
          disableHighlightToday
          value={selectedDay}
          timezone={companyContext.company.timezone}
          onChange={(value) => {
            if (!value) return;
            if (
              selectedDay &&
              !DateUtils.isSame(value, selectedDay, 'month', { timezone: companyContext.company.timezone })
            )
              return;
            logDebug('Day change - setting `selectedDay` to `value`:', {
              value,
              selectedDay
            });
            setSelectedDay(value);
          }}
          onMonthChange={(value) => {
            if (!value) return;
            setSelectedMonth(value);
            setSelectedDay(null);
          }}
        />
      </Card>
      <Card
        variant='outlined'
        sx={{
          display: 'flex',
          position: 'relative',
          minWidth: fullScreen ? '100%' : '50%',
          height: fullScreen ? 'fill-available' : '338px'
        }}>
        <Stack spacing={0} width='100%'>
          <Typography
            sx={{ alignSelf: 'center', paddingY: 1 }}
            variant='body1Bold'
            text={
              selectedDay
                ? DateUtils.format(selectedDay, DateFormatEnum.DateUnabbreviatedWeekday, {
                    timezone: companyContext.company.timezone
                  })
                : 'Select a day'
            }
          />
          <Divider />
          <List sx={{ overflow: 'auto', width: '100%' }} disablePadding>
            {(allAvailableQuery?.data || [])
              .sort(sortCapacityItems)
              .filter((capacityItem) => {
                if (!selectedDay) return false;

                const startOfSelectedDay = DateUtils.toStartOf(selectedDay, 'day', { timezone: 'utc' });
                const endOfSelectedDay = DateUtils.toEndOf(selectedDay, 'day', { timezone: 'utc' });

                const capacityItemStartDate = new Date(capacityItem.start);
                const capacityItemEndDate = new Date(capacityItem.end);
                return (
                  DateUtils.isAfterOrEqual(capacityItemStartDate, startOfSelectedDay) &&
                  DateUtils.isBefore(capacityItemEndDate, endOfSelectedDay)
                );
              })
              .map((capacityItem, index) => (
                <AvailabilityListItem
                  key={index}
                  divider
                  companyContext={companyContext}
                  displayMode={'allAvailable'}
                  selectedCapacityItem={selectedCapacityItem}
                  capacityItem={capacityItem}
                  setCapacityItem={setCapacityItem}
                />
              ))}
          </List>
        </Stack>
      </Card>
    </FlexBox>
  );
};

const dialogContentTextSx = { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 };

const GetAvailabilityDialog = (props: GetAvailabilityDialogProps) => {
  const {
    customer,
    location,
    jobType,
    businessUnit,
    companyContext,
    durationStart,
    onClose,
    titleText,
    container,
    ...dialogProps
  } = props;

  const [toggleValue, setToggleValue] = useState<'firstAvailable' | 'allAvailable'>('firstAvailable');

  const [startOfCall] = useState(new Date(durationStart));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [incrementStartingMonth, setIncrementStartingMonth] = useState(false);

  const [selectedCapacityItem, selectCapacityItem] = useState<ServiceTitanAdjustableCapacityDTO | null>(null);

  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  return (
    <DialogWrapper
      onClose={onClose}
      titleText={titleText || 'Get Availability'}
      fullWidth
      maxWidth='md'
      fullScreen={fullScreen}
      container={!fullScreen ? container : undefined}
      {...dialogProps}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        <Stack useFlexGap overflow='auto' spacing={2}>
          <Stack direction='row' useFlexGap justifyContent='center' width='100%'>
            <Typography sx={[dialogContentTextSx, { fontSize: '1.125rem' }]}>
              <PersonIcon />
              {customerDisplayName(customer)}
            </Typography>
            <VerticalDivider />
            <Typography sx={[dialogContentTextSx, { whiteSpace: 'pre-line', fontSize: '1.125rem' }]}>
              {customer.isCommercial ? <BusinessIcon /> : <PlaceIcon />}
              {formatAddressOneLine(location)}
            </Typography>
          </Stack>
          <Stack direction='row' useFlexGap justifyContent='center'>
            <Typography sx={dialogContentTextSx}>
              <WorkIcon />
              {jobType.name}
            </Typography>
            <VerticalDivider />
            <Typography sx={dialogContentTextSx}>
              <TimelapseIcon />
              {`${jobType.duration ? Math.floor(jobType.duration / 3600) : 0} hour(s)`}
            </Typography>
          </Stack>
          <ToggleButtonGroup
            size='small'
            value={toggleValue}
            exclusive
            onChange={(_e, value) => value && setToggleValue(value)}
            sx={{
              alignSelf: 'center'
            }}>
            <ToggleButton value='firstAvailable' sx={{ gap: 0.5 }}>
              <ListIcon />
              {'First Available'}
            </ToggleButton>
            <ToggleButton value='allAvailable' sx={{ gap: 0.5 }}>
              <InsertInvitationIcon />
              {'All Available'}
            </ToggleButton>
          </ToggleButtonGroup>
          {toggleValue === 'firstAvailable' && (
            <FirstAvailable
              jobType={jobType}
              businessUnit={businessUnit}
              companyContext={companyContext}
              startOfCall={startOfCall}
              selectedCapacityItem={selectedCapacityItem}
              setCapacityItem={selectCapacityItem}
              setIncrementStartingMonth={setIncrementStartingMonth}
            />
          )}
          {toggleValue === 'allAvailable' && (
            <AllAvailable
              jobType={jobType}
              businessUnit={businessUnit}
              companyContext={companyContext}
              startOfCall={startOfCall}
              selectedDay={selectedDay}
              incrementStartingMonth={incrementStartingMonth}
              fullScreen={fullScreen}
              setSelectedDay={setSelectedDay}
              selectedCapacityItem={selectedCapacityItem}
              setCapacityItem={selectCapacityItem}
            />
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <DialogButton color='cancel' onClick={() => onClose({ action: 'CANCEL' })} text='Cancel' />
        <DialogButton
          disabled={!selectedCapacityItem}
          onClick={() => {
            if (!selectedCapacityItem) return;
            onClose({ action: 'CONFIRM', value: { capacityItem: selectedCapacityItem } });
          }}
          text='Confirm'
        />
      </DialogActions>
    </DialogWrapper>
  );
};

interface GetAvailabilityDialog
  extends AsyncDialog<{ capacityItem: ServiceTitanAdjustableCapacityDTO }, GetAvailabilityDialogProps> {}
export const useGetAvailabilityDialog = (): GetAvailabilityDialog => useAsyncDialog(GetAvailabilityDialog);
